import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onSessionExpired } from "../../utils/session-events";
import { hasFormData } from "../../utils/formCache";
import { Button } from "../ui/button";
import { ProgressBar } from "../ui/feedback";
import { Modal } from "../ui/modal";

const COUNTDOWN_SECONDS = 10;

/**
 * 会话过期提示：401 拦截器在静默续签失败后触发 `onSessionExpired`，这里给出 10 秒倒计时，
 * 用户可立即重新登录，也可让倒计时结束后自动跳转——避免「正在填表单被瞬间踢走」。
 * 提示里明确告知已缓存未提交内容（缓存由 utils/formCache 与 useFormAutoSave 负责）。
 */
export function useSessionExpiryModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    return onSessionExpired(() => {
      setSeconds(COUNTDOWN_SECONDS);
      setOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (open && seconds <= 0) {
      setOpen(false);
      navigate("/login", { replace: true });
    }
  }, [open, seconds, navigate]);

  const modal = (
    <Modal
      open={open}
      title="登录已过期"
      width={420}
      onClose={() => setOpen(false)}
      zIndex={90}
      footer={
        <>
          <Button onClick={() => setOpen(false)}>稍后处理</Button>
          <Button
            variant="primary"
            onClick={() => {
              setOpen(false);
              navigate("/login", { replace: true });
            }}
          >
            立即重新登录
          </Button>
        </>
      }
    >
      <div className="session-expiry">
        <p>
          为保护数据安全，登录凭据已失效。{hasFormData() ? "检测到有未提交的表单草稿，已自动保存，重新登录后可继续。" : "重新登录后即可继续操作。"}
        </p>
        <ProgressBar value={(seconds / COUNTDOWN_SECONDS) * 100} tone={seconds <= 3 ? "red" : "brand"} height={5} showLabel={false} />
        <div className="session-expiry-count">{seconds} 秒后自动跳转到登录页</div>
      </div>
    </Modal>
  );

  return { modal };
}

export default useSessionExpiryModal;
